import { ArrowLeft, Building2, ChevronRight } from 'lucide-react';

import { FormError, Spinner } from './ui';
import { roleLabel } from '../lib/roles';
import type { CompanyMembership } from '../lib/auth/types';
import { t } from '../lib/i18n';

/**
 * Paso "¿En qué empresa vas a trabajar?" del login, cuando el usuario pertenece
 * a más de una empresa. Presentacional: recibe las empresas y delega la
 * selección. Comparte la tarjeta y los tokens de marca con el login.
 */
export function CompanySelectStep({
  companies,
  selectingId,
  error,
  onSelect,
  onBack,
}: {
  companies: CompanyMembership[];
  selectingId: string | null;
  error?: string;
  onSelect: (companyId: string) => void;
  onBack: () => void;
}) {
  const busy = selectingId !== null;

  return (
    <div className="hero-rise relative overflow-hidden rounded-3xl border border-line bg-surface p-6 shadow-card sm:p-8">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-28"
        style={{
          background:
            'radial-gradient(80% 100% at 50% 0%, color-mix(in srgb, var(--brand) 11%, transparent), transparent 74%)',
        }}
      />

      <div className="relative">
        <div className="mb-6 space-y-1.5">
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-ink sm:text-[1.75rem]">
            {t('company.select.title')}
          </h1>
          <p className="text-sm text-muted">{t('company.select.subtitle')}</p>
        </div>

        {error && (
          <div className="mb-4">
            <FormError>{error}</FormError>
          </div>
        )}

        <ul className="space-y-2.5" aria-busy={busy}>
          {companies.map((c) => {
            const isBusy = selectingId === c.id;
            return (
              <li key={c.id}>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onSelect(c.id)}
                  className="group flex w-full items-center gap-3 rounded-token border border-line bg-surface-2 px-4 py-3.5 text-left transition-all hover:-translate-y-0.5 hover:border-brand hover:shadow-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand disabled:pointer-events-none disabled:opacity-60"
                >
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-token bg-brand/10 text-brand">
                    <Building2 size={18} aria-hidden />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-display font-semibold text-ink">
                      {c.name}
                    </span>
                    <span className="block truncate font-mono text-[11px] uppercase tracking-[0.14em] text-muted">
                      {roleLabel(c.role)}
                    </span>
                  </span>
                  {isBusy ? (
                    <Spinner className="h-4 w-4 shrink-0" />
                  ) : (
                    <ChevronRight
                      size={18}
                      aria-hidden
                      className="shrink-0 text-muted transition-transform group-hover:translate-x-0.5 group-hover:text-brand"
                    />
                  )}
                </button>
              </li>
            );
          })}
        </ul>

        <button
          type="button"
          onClick={onBack}
          disabled={busy}
          className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-muted transition-colors hover:text-brand disabled:opacity-60"
        >
          <ArrowLeft size={16} aria-hidden />
          {t('company.select.back')}
        </button>
      </div>
    </div>
  );
}
